import ContentElement from './content-element.js';
import { default as PhotoSwipe, type PhotoSwipeOptions, type SlideData, type PhotoSwipeEventsMap } from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/lightbox';

const defaultPhotoswipeOptions: PhotoSwipeOptions = {
    pswpModule: PhotoSwipe,
    bgOpacity: 0.8,
    allowPanToNext: true,
    spacing: 0.1,
    loop: true,
    pinchToClose: true,
    closeOnVerticalDrag: true,
    hideAnimationDuration: 333,
    showAnimationDuration: 333,
    zoomAnimationDuration: 333,
    wheelToZoom: false,
    escKey: true,
    arrowKeys: true,
    trapFocus: true,
    returnFocus: true,
    maxWidthToAnimate: 4000,
    clickToCloseNonZoomable: true,
    imageClickAction: 'zoom-or-close',
    bgClickAction: 'close',
    tapAction: 'toggle-controls',
    doubleTapAction: 'zoom',
    indexIndicatorSep: ' / ',
    preloaderDelay: 2000,
    index: 0,
    preload: [1, 2],
    easing: 'cubic-bezier(.4,0,.22,1)',
};

const DEFAULT_ELEMENT_SELECTOR: string = '.th-fsc-gallery-lightbox';

class Gallery extends ContentElement {
    protected options: PhotoSwipeOptions;

    public override init() {
        this.options = this.assignConfig<PhotoSwipeOptions>(defaultPhotoswipeOptions, this.config, undefined, true);
        this.options.closeTitle = window.ThiemeCore.Lang.translate('closeTitle', null, this.options.closeTitle, 'photoswipe');
        this.options.zoomTitle = window.ThiemeCore.Lang.translate('zoomTitle', null, this.options.zoomTitle, 'photoswipe');
        this.options.arrowPrevTitle = window.ThiemeCore.Lang.translate('arrowPrevTitle', null, this.options.arrowPrevTitle, 'photoswipe');
        this.options.arrowNextTitle = window.ThiemeCore.Lang.translate('arrowNextTitle', null, this.options.arrowNextTitle, 'photoswipe');
        this.options.indexIndicatorSep = window.ThiemeCore.Lang.translate('indexIndicatorSep', null, this.options.indexIndicatorSep, 'photoswipe');
        this.options.errorMsg = window.ThiemeCore.Lang.translate('errorMsg', null, this.options.errorMsg, 'photoswipe');
    }

    public override process() {
        const dataSource: SlideData[] = [];

        this.element.querySelectorAll<HTMLElement>('.items .item').forEach((el: HTMLElement) => {
            dataSource.push({
                src: el.getAttribute('href') ?? el.dataset.src ?? '',
                caption: el.dataset.caption ?? '',
                copyright: el.dataset.copyright ?? '',
                width: 0,
                height: 0,
                srcset: '',
                alt: '',
                thumb: el.dataset.thumb ?? '',
                el: el
            });
        });

        const options: PhotoSwipeOptions = Object.assign({}, this.options, {
            dataSource: dataSource
        });


        const lightboxInstance: PhotoSwipeLightbox = new PhotoSwipeLightbox(options);

        lightboxInstance.on('contentLoadImage', (ev: PhotoSwipeEventsMap["contentLoadImage"]) => {
            if(ev.content.element) {
                ev.content.element.style.width = '';
                ev.content.element.style.height = '';
            }
        });
        lightboxInstance.on('uiRegister', () => {
            if(typeof(lightboxInstance.pswp) == 'undefined') {
                return;
            }

            lightboxInstance.pswp.ui?.registerElement({
                name: 'custom-caption',
                order: 9,
                isButton: false,
                appendTo: 'root',
                html: '',
                onInit: function(captionElement: HTMLElement, pswp: PhotoSwipe) {
                    captionElement.style.display = 'none';
                    lightboxInstance.pswp?.on('change', function() {
                        captionElement.innerHTML = '';
                        captionElement.style.display = 'none';
                        if(lightboxInstance.pswp?.currSlide?.data.copyright && lightboxInstance.pswp?.currSlide?.data.copyright != '') {
                            captionElement.innerHTML += '<div class="credits"><div class="copyright">'+lightboxInstance.pswp.currSlide.data.copyright+'</div></div>';
                        }
                        if(lightboxInstance.pswp?.currSlide?.data.caption && lightboxInstance.pswp?.currSlide?.data.caption != '') {
                            captionElement.innerHTML += '<div class="caption text-center">'+lightboxInstance.pswp.currSlide.data.caption+'</div>';
                        }

                        if(captionElement.innerHTML != '') {
                            captionElement.style.display = 'block';
                        }
                    });
                }
            });
        });

        lightboxInstance.init();

        const controlEl = this.element.querySelector<HTMLElement>('[data-action="showGallery"]');
        if(controlEl) {
            controlEl.addEventListener('click', (ev: MouseEvent) => {
                ev.preventDefault();
                lightboxInstance.loadAndOpen(0);
                return false;
            });
        }
    }

}

// Observe
Gallery.initializeDom(DEFAULT_ELEMENT_SELECTOR, true, false, true);

export default Gallery;
